<template>
  <detra-modal :sidebar="false" :show="show_modal" @hide="hide()">
    <div class="p-24">
      <div class="flex align-items-center" style="padding-right: 10rem;">
        <div class="heading-medium">
          <skeleton-text :until="project">
            <div v-if="project" >{{ project.name }}</div>
          </skeleton-text>
        </div>
        <dot-button v-if="$root.isAdmin">
          <div class="dropdown-button" @click="edit()">Edit project</div>
          <div class="dropdown-button" @click="destroy()">Delete project</div>
        </dot-button>
      </div>
      <div class="color-gray-500">Project</div>
    </div>
    <div class="p-24 pt-0">
      <skeleton-text :until="project">
        <div v-if="project">
          <table class="w-100">
            <tr>
              <td>Client</td>
              <td>Deadline</td>
              <td>
                <span v-if="project.fixed_price">Fixed price</span>
                <span v-if="!project.fixed_price">Billed hours</span>

              </td>
              <td>Duration</td>
            </tr>
            <tr>
              <td class="w-25">
                <div class="heading-small">{{ project.client.name }}</div>
                <div class="color-gray-500">Responsible manager - {{ project.client.manager ? project.client.manager.name : 'Not set' }}</div>
              </td>
              <td class="w-25">
                <div class="heading-small">{{ _project_deadline || '-' }}</div>
              </td>
              <td class="w-25">
                <div
                  class="heading-small"
                  v-if="$root.isSimple">
                  {{ project.fixed_price ? 'Done on fixed price' : 'Done on hourly rate' }}
                </div>
                <div v-if="!$root.isSimple">
                  <div class="heading-small">{{ project.fixed_price ? project.fixed_price : (project.billed_hours_formatted || '-') }}</div>
                  <div v-if="!project.fixed_price"> {{ project.billed_hours / 3600 * project.client.price_per_hour }} Eur</div>
                </div>
              </td>
              <td class="w-25">
                <div class="heading-small">{{ project.duration_formatted || '-' }}</div>
              </td>
            </tr>
          </table>
        </div>
      </skeleton-text>
    </div>
    <div class="p-24 pt-0" v-if="project && !project.billed_at && $root.isAdmin">
      <div class="text-right" v-if="!project_billing">
        <button class="btn btn-jet w-100" @click="toggleProjectBilling()" v-skeleton-button="!project">Bill project</button>
      </div>
      <div class="flex w-100" v-if="project_billing">
        <div class="w-25 pr-4 align-items-end flex">
          <button class="btn btn-silent w-100" @click="toggleProjectBilling()" v-skeleton-button="!project">Cancel</button>
        </div>
        <div class="w-25 pr-4 align-items-center flex flex-wrap">
          <div v-if="project && !project.fixed_price" class="w-100">
            <span class="w-100">Hours to bill</span>
            <input type="text" class="form-control" v-model="hours_to_bill" v-skeleton-input="!project">
          </div>
          <div v-if="project && project.fixed_price" class="text-center w-100">
            Confirm billing?
          </div>
        </div>
        <div class="pr-4 w-25 align-items-end flex" v-if="project">
          <div class="w-100">
          <span class="w-100">Bill at</span>
          <date-picker
            @dateSelected="billed_at = $event"
            :dateFormat="'dd M yyyy'"
            :defaultDate="billed_at"
            @blur="$v.billed_at.$touch()"
          ></date-picker>
          </div>
        </div>
        <div class="w-25 align-items-end flex">
          <button class="btn btn-jet w-100" @click="billProject()" v-skeleton-button="!project">Bill</button>
        </div>
      </div>
    </div>
    <div class="p-24 pt-0" v-if="project && project.billed_at && $root.isAdmin">
      <div class="text-right">
        <button class="btn btn-jet w-100" @click="unbillProject()" v-skeleton-button="!project">Unbill project</button>
      </div>
    </div>
    <div class="divider" v-if="project && project.description"></div>
    <div class="p-24" v-if="project && project.description">
      <div class="heading-medium">Description</div>
      <div class="mt-16" v-html="project.description_html"></div>
    </div>
    <div class="divider"></div>
    <div class="p-24">
      <div class="heading-medium">Tasks overview</div>
      <div class="mt-16">
        <skeleton-text :until="project">
          <div v-if="project">
            <table class="w-100 tasks-table">
              <tr>
                <td class="heading-small">Task name</td>
                <td class="heading-small">Assigned to</td>
                <td class="heading-small">Foreseen / Assigned / Completed</td>
              </tr>
              <tr v-for="task in project.tasks">
                <td>
                  <div>
                    {{ task.name }}
                    <span class="check-circle" v-if="task.is_completed">
                      <i class="fas fa-check"></i>
                    </span>
                  </div>
                  <div>
                  </div>
                </td>
                <td>{{ task.assigned_to }}</td>
                <td>
                  {{ task.planed_hours_formatted }} / {{ task.assigned_hours_formatted }} / {{ task.completed_hours_formatted }}
                </td>
              </tr>
              <tr>
                <td class="heading-small">{{ project.task_count }} tasks</td>
                <td></td>
                <td class="heading-small font-weight-bold">{{ project.total_planned_hours_formatted }} / {{ project.total_assigned_hours_formatted }} / {{ project.total_completed_hours_formatted }}</td>
              </tr>
            </table>
          </div>
        </skeleton-text>
      </div>
    </div>
    <div class="divider"></div>
    <div class="p-24">
      <div class="heading-medium">Project progress</div>
      <div class="flex mt-16">
        <div class="color-grey-500 flex-grow-1">Completed tasks:
          <skeleton-text :inline="true" :until="project">
            <div v-if="project">{{ project.completed_tasks }}</div>
          </skeleton-text>
        </div>
        <div class="color-grey-500">Total tasks:
          <skeleton-text :inline="true" :until="project">
            <div v-if="project">{{ project.task_count }}</div>
          </skeleton-text>
        </div>
      </div>
      <progress-bar v-if="project" class="mt-1" style="height: 8px;" :fill="project.completion_percentage"></progress-bar>
    </div>
    <div class="divider"></div>
    <div class="p-24">
      <div class="heading-medium">Recent project activity</div>
      <skeleton-text :until="project">
        <div v-if="project"></div>
      </skeleton-text>
    </div>
  </detra-modal>
</template>
<script>

import DotButton from '../../../../components/dot-button'
import ProgressBar from '../../../../components/progress-bar'
import Datepicker from "../../../../components/partials/Datepicker";

export default {
  components: {
    DotButton,
    ProgressBar,
    'date-picker': Datepicker,
  },
  created() {
    this.$root.$on('ProjectModal::show-project', ({project_id}, callback) => {
      this.show()
      callback && callback()

      this.axios.get('/n/projects/' + project_id + '?show=true').then(response => {
        this.project = response.data.data
      })
    })

    this.billed_at = moment().format('DD MMM YYYY')
  },
  data() {
    return {
      show_modal: false,
      project: null,
      project_billing: false,
      hours_to_bill: null,
      billed_at: null,
    }
  },
  computed: {
    _project_deadline() {
      return this.project.deadline ? moment(this.project.deadline).format('YYYY-MM-DD') : null
    }
  },
  methods: {
    show() {
      this.show_modal = true
    },
    hide() {
      this.toggleProjectBilling(false)
      this.show_modal = false
    },
    edit() {
      this.$root.$emit('ProjectModal::edit-project', {project_id: this.project.id})
      this.hide()
    },
    destroy() {
      if (!confirm('Are you sure?')) return

      this.axios.delete('/n/projects/' + this.project.id).then(response => {
        this.$root.$emit('Projects::deleted', {project: this.project})
        this.hide()
      })
    },
    toggleProjectBilling(show = null) {
      this.project_billing = show !== null ? show : !this.project_billing
      if (this.project_billing) {
        this.hours_to_bill = this.project.fixed_price ? null : this.project.total_completed_hours_formatted
      }
    },
    billProject() {
      this.axios.post('/n/projects/bill', {
        project_id: this.project.id,
        billed_hours: this.hours_to_bill,
        billed_at: this.billed_at,
      }).then(response => {
        this.$awn.success('Project billed successfully!')
        this.$root.$emit('Agenda::reload')
        this.$root.$emit('ClientList::reload')
        this.$root.$emit('ClientProjects::reload')
        this.$root.$emit('ProjectView::reload')
        this.$root.$emit('Projects::updated', {project: response.data.data})
        this.hide()
      })
    },
    unbillProject() {
      this.axios.post('/n/projects/unbill', {
        project_id: this.project.id,
      }).then(response => {
        this.$awn.success('Project un-billed.')
        this.$root.$emit('Agenda::reload')
        this.$root.$emit('ClientList::reload')
        this.$root.$emit('ClientProjects::reload')
        this.$root.$emit('ProjectView::reload')
        this.$root.$emit('Projects::updated', {project: response.data.data})
        this.hide()
      })
    },
  }
}
</script>
<style lang="scss">

.check-circle {
  background: #37BB64;
  border-radius: 100%;
  color: white;
  padding: 0 5px 1px 4px;


  .fas {
    font-size: 8px;
  }
}

.tasks-table {
  tr {
    td, th {
      padding: 12px;

      &:first-child {
        padding-left: 0;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/agenda-v2/task-pool/project-modal/show-project.vue